import {
  readingsDiffer,
  staleZones,
  type DateTimeFormatConstructor,
  type TimeZoneRules,
} from "../model/rules";
import {loadIntlPolyfill} from "./loadIntlPolyfill";

export type {TimeZoneRules};

/**
 * How long the bundled rules may take to load. A network that hangs (a clinic's Wi-Fi, a proxy) would otherwise
 * leave the page blank with nothing said; past it, the rules are stale and the person is told.
 */
export const LOAD_TIMEOUT_MS = 10_000;

/**
 * Settles the rules: the browser's own when they give every case right; else the bundled ones, loaded and checked
 * again. The browser's own `Intl` and the loader are passed in, so it can be tested without either.
 */
export async function settleTimeZoneRules({
  native,
  current,
  load,
  timeoutMs = LOAD_TIMEOUT_MS,
}: {
  /** The browser's own `Intl.DateTimeFormat`, taken before anything replaces it. */
  readonly native: DateTimeFormatConstructor;
  /** `Intl.DateTimeFormat` as it is now (after the loader, the polyfill's). */
  readonly current: () => DateTimeFormatConstructor;
  readonly load: (localZone: string | null) => Promise<void>;
  /** How long the loader may take (`LOAD_TIMEOUT_MS`). */
  readonly timeoutMs?: number;
}): Promise<TimeZoneRules> {
  const staleCases = staleZones(native);
  if (!staleCases.length)
    return {state: "current", staleCases, localZoneStale: null};
  let localZone: string | null = null;
  try {
    localZone = new native().resolvedOptions().timeZone || null;
  } catch {
    localZone = null;
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      load(localZone),
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () => reject(new Error("TIME_ZONE_RULES_LOAD_TIMEOUT")),
          timeoutMs
        );
      }),
    ]);
  } catch {
    return {state: "stale", staleCases, localZoneStale: localZone};
  } finally {
    clearTimeout(timer);
  }
  const now = current();
  if (now === native || staleZones(now).length)
    return {state: "stale", staleCases, localZoneStale: localZone};
  return {
    state: "polyfilled",
    staleCases,
    localZoneStale:
      localZone && readingsDiffer(native, now, localZone) ? localZone : null,
  };
}

let settled: TimeZoneRules | null = null;
let settling: Promise<TimeZoneRules> | null = null;

/**
 * The app's guard, once (index.jsx, before the first render): the browser's rules, replaced where they are out of
 * date. No time is drawn before it settles, so nothing reads the browser's own rules where they are wrong.
 */
export function ensureTimeZoneRules(): Promise<TimeZoneRules> {
  settling ??= settleTimeZoneRules({
    native: Intl.DateTimeFormat,
    current: () => Intl.DateTimeFormat,
    load: loadIntlPolyfill,
  }).then((rules) => (settled = rules));
  return settling;
}

/** What the guard settled on; null before it has. */
export function timeZoneRules(): TimeZoneRules | null {
  return settled;
}
